import axios from "axios";

// Webhook configuration
const WEBHOOK_CONFIG = {
    baseURL: "https://webhook.site/427d7f1c-5c86-498c-b25a-4f5f31afd6a2",
    headers: { "Content-Type": "application/json" },
    method: "post",
    timeout: 10000,
};

// Create axios instance
const webhookInstance = axios.create(WEBHOOK_CONFIG);

/**
 * Standardizes and sends data to webhook
 * @param {Object} formData - The form data to send
 * @param {string} event - The event type (e.g., 'lead', 'subscriber', 'pdfDownload')
 * @param {Object} options - Additional options
 * @param {boolean} options.pushToDataLayer - Whether to push to window.dataLayer (default: true)
 * @param {boolean} options.trackReferrals - Whether to track referral state (default: true)
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @param {Function} options.onFinally - Finally callback
 * @returns {Promise}
 */
export const sendToWebhook = async (formData, event, options = {}) => {
    const {
        pushToDataLayer = true,
        trackReferrals = true,
        onSuccess,
        onError,
        onFinally
    } = options;

    try {
        // Get query parameters from sessionStorage
        const queryParams = JSON.parse(sessionStorage.getItem("queryParams") || "{}");

        // Build details string from query parameters
        const lines = [];
        for (const key in queryParams) {
            if (queryParams[key]) {
                lines.push(`*${key}:* ${queryParams[key]}`);
            }
        }

        // Get referral UUID from window.tolt_referral if available
        const referralUuid = typeof window !== 'undefined' && window.tolt_referral ? window.tolt_referral : null;

        let referralState = null;

        // Track referral state if enabled and referral exists
        if (trackReferrals && referralUuid && formData.email && typeof window !== 'undefined' && window.tolt && typeof window.tolt.signup === 'function') {
            try {
                const result = await window.tolt.signup(formData.email);
                // JSON stringify and base64 encode the signup result using browser's btoa
                referralState = btoa(JSON.stringify(result));
                lines.push(`*referralState:* ${referralState}`);
            } catch (err) {
                // Handle 409 Conflict (already signed up) or other errors gracefully
                console.log('Tolt signup skipped:', err.message || 'User may already be signed up');
                // Continue without referral state if signup fails - we still have referralUuid
            }
        }

        // Standardize the payload
        const standardizedPayload = {
            event,
            source: "orders.co",
            utm_source: queryParams?.utm_source || "",
            referralUuid, // Add referral UUID to standardized data
            referralState, // Add referral state
            ...formData, // Spread the form data
            details: lines.join("\n")
        };

        // Send to webhook
        const response = await webhookInstance.request({
            data: JSON.stringify(standardizedPayload)
        });

        // Push to dataLayer if enabled
        if (pushToDataLayer && typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push(standardizedPayload);
        }

        // Call success callback
        if (onSuccess) {
            onSuccess(response, standardizedPayload);
        }

        console.log(`${event} form was submitted successfully`);
        return { success: true, data: standardizedPayload };

    } catch (error) {
        console.error(`Error submitting ${event} form:`, error);

        // Call error callback
        if (onError) {
            onError(error);
        }

        return { success: false, error };
    } finally {
        // Call finally callback
        if (onFinally) {
            onFinally();
        }
    }
};

/**
 * Common form validation utilities
 */
export const formValidation = {
    isEmailValid: (email) => {
        const re = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
        return re.test(String(email).toLowerCase());
    },

    isNotEmptyString: (value) => {
        const re = /^(?!\s*$).+/;
        return re.test(value);
    },

    validateRequiredFields: (fields, validationRules = {}) => {
        const errors = {};
        let isValid = true;

        for (const field in fields) {
            const value = fields[field];
            const rules = validationRules[field] || {};

            if (rules.required && (!value || value.trim() === '')) {
                errors[field] = rules.requiredMessage || `${field} is required`;
                isValid = false;
            } else if (rules.email && !formValidation.isEmailValid(value)) {
                errors[field] = rules.emailMessage || 'Please enter a valid email';
                isValid = false;
            } else if (rules.notEmpty && !formValidation.isNotEmptyString(value)) {
                errors[field] = rules.notEmptyMessage || `${field} cannot be empty`;
                isValid = false;
            }
        }

        return { isValid, errors };
    }
};

/**
 * Pre-configured webhook calls for common events
 */
export const webhookEvents = {
    lead: (formData, options) => sendToWebhook(formData, 'lead', options),
    subscriber: (formData, options) => sendToWebhook(formData, 'subscriber', options),
    pdfDownload: (formData, options) => sendToWebhook(formData, 'pdfDownload', options),
    contact: (formData, options) => sendToWebhook(formData, 'contact', options),
};

export default {
    sendToWebhook,
    webhookEvents,
    formValidation
};